import { useState, Fragment } from 'react';
import { Menu, Transition } from '@headlessui/react';
import {
  StarIcon,
  EllipsisHorizontalIcon,
  PlusIcon,
  FunnelIcon,
  Bars3BottomLeftIcon,
  UserPlusIcon,
  ShareIcon,
  LinkIcon,
  TrashIcon,
  DocumentDuplicateIcon,
  ArrowDownTrayIcon,
} from '@heroicons/react/24/outline';
import { StarIcon as StarIconSolid } from '@heroicons/react/24/solid';

interface BoardHeaderProps {
  board: {
    id: string;
    name: string;
    description?: string;
    isFavorite?: boolean;
    memberCount?: number;
  };
  onAddItem?: () => void;
  onFilter?: () => void;
  onSort?: () => void;
  onInvite?: () => void;
  onShare?: () => void;
  onToggleFavorite?: () => void;
}

type ViewType = 'table' | 'kanban' | 'calendar' | 'timeline';

const VIEW_TABS: { id: ViewType; label: string }[] = [
  { id: 'table', label: 'Main Table' },
  { id: 'kanban', label: 'Kanban' },
  { id: 'calendar', label: 'Calendar' },
  { id: 'timeline', label: 'Timeline' },
];

export function BoardHeader({
  board,
  onAddItem,
  onFilter,
  onSort,
  onInvite,
  onShare,
  onToggleFavorite,
}: BoardHeaderProps) {
  const [activeView, setActiveView] = useState<ViewType>('table');
  const [isEditingName, setIsEditingName] = useState(false);
  const [editedName, setEditedName] = useState(board.name);

  const handleNameSubmit = () => {
    setIsEditingName(false);
    console.log('Update board name to:', editedName);
  };

  return (
    <div className="bg-white/5 backdrop-blur-xl border-b border-white/10">
      {/* Top section: Board name and actions */}
      <div className="flex items-center justify-between px-6 py-4">
        <div className="flex items-center gap-3">
          {/* Board name */}
          {isEditingName ? (
            <input
              type="text"
              value={editedName}
              onChange={(e) => setEditedName(e.target.value)}
              onBlur={handleNameSubmit}
              onKeyDown={(e) => e.key === 'Enter' && handleNameSubmit()}
              className="text-xl font-bold text-gray-100 bg-transparent border-b-2 border-primary-500 outline-none px-1"
              autoFocus
            />
          ) : (
            <h1
              className="text-xl font-bold text-gray-100 cursor-pointer hover:text-primary-400"
              onClick={() => setIsEditingName(true)}
            >
              {board.name}
            </h1>
          )}

          {/* Favorite button */}
          <button
            onClick={onToggleFavorite}
            className="p-1.5 rounded-md hover:bg-white/10 text-gray-500 hover:text-yellow-500"
            title={board.isFavorite ? 'Remove from favorites' : 'Add to favorites'}
          >
            {board.isFavorite ? (
              <StarIconSolid className="h-5 w-5 text-yellow-500" />
            ) : (
              <StarIcon className="h-5 w-5" />
            )}
          </button>

          {/* Board menu */}
          <BoardOptionsMenu board={board} />
        </div>

        {/* Action buttons */}
        <div className="flex items-center gap-2">
          {/* Member avatars */}
          <div className="flex -space-x-2 mr-2">
            {[1, 2, 3].map((i) => (
              <div
                key={i}
                className="h-8 w-8 rounded-full bg-primary-500/20 border-2 border-[#0a0a0f] flex items-center justify-center"
              >
                <span className="text-xs font-medium text-primary-400">U{i}</span>
              </div>
            ))}
            {board.memberCount && board.memberCount > 3 && (
              <div className="h-8 w-8 rounded-full bg-white/10 border-2 border-[#0a0a0f] flex items-center justify-center">
                <span className="text-xs font-medium text-gray-400">+{board.memberCount - 3}</span>
              </div>
            )}
          </div>

          {/* Invite button */}
          <button
            onClick={onInvite}
            className="btn-secondary flex items-center gap-2"
          >
            <UserPlusIcon className="h-4 w-4" />
            <span className="hidden sm:inline">Invite</span>
          </button>

          {/* Share button */}
          <button
            onClick={onShare}
            className="btn-ghost flex items-center gap-2"
          >
            <ShareIcon className="h-4 w-4" />
            <span className="hidden sm:inline">Share</span>
          </button>
        </div>
      </div>

      {/* Description (if exists) */}
      {board.description && (
        <div className="px-6 pb-3">
          <p className="text-sm text-gray-500">{board.description}</p>
        </div>
      )}

      {/* Bottom section: View tabs and toolbar */}
      <div className="flex items-center justify-between px-6 py-2 border-t border-white/5">
        {/* View tabs */}
        <div className="flex items-center gap-1">
          {VIEW_TABS.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveView(tab.id)}
              className={`
                px-3 py-1.5 text-sm font-medium rounded-md transition-colors
                ${activeView === tab.id
                  ? 'bg-primary-500/15 text-primary-400'
                  : 'text-gray-400 hover:bg-white/10 hover:text-gray-200'
                }
              `}
            >
              {tab.label}
            </button>
          ))}
          <button className="p-1.5 text-gray-500 hover:text-gray-300 hover:bg-white/10 rounded-md">
            <PlusIcon className="h-4 w-4" />
          </button>
        </div>

        {/* Toolbar */}
        <div className="flex items-center gap-2">
          {/* New item button */}
          <button
            onClick={onAddItem}
            className="btn-primary flex items-center gap-2 py-1.5"
          >
            <PlusIcon className="h-4 w-4" />
            <span>New Item</span>
          </button>

          {/* Filter */}
          <button
            onClick={onFilter}
            className="btn-ghost flex items-center gap-2 py-1.5"
          >
            <FunnelIcon className="h-4 w-4" />
            <span className="hidden sm:inline">Filter</span>
          </button>

          {/* Sort */}
          <button
            onClick={onSort}
            className="btn-ghost flex items-center gap-2 py-1.5"
          >
            <Bars3BottomLeftIcon className="h-4 w-4" />
            <span className="hidden sm:inline">Sort</span>
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * Board Options Dropdown Menu
 */
function BoardOptionsMenu({ board: _board }: { board: { id: string; name: string } }) {
  return (
    <Menu as="div" className="relative">
      <Menu.Button className="p-1.5 rounded-md hover:bg-white/10 text-gray-500">
        <EllipsisHorizontalIcon className="h-5 w-5" />
      </Menu.Button>

      <Transition
        as={Fragment}
        enter="transition ease-out duration-100"
        enterFrom="transform opacity-0 scale-95"
        enterTo="transform opacity-100 scale-100"
        leave="transition ease-in duration-75"
        leaveFrom="transform opacity-100 scale-100"
        leaveTo="transform opacity-0 scale-95"
      >
        <Menu.Items className="absolute left-0 mt-2 w-56 dropdown-glass">
          <Menu.Item>
            {({ active }) => (
              <button
                className={`w-full flex items-center gap-3 px-4 py-2 text-sm ${active ? 'bg-white/10' : ''} text-gray-300`}
              >
                <LinkIcon className="h-4 w-4 text-gray-500" />
                Copy board link
              </button>
            )}
          </Menu.Item>
          <Menu.Item>
            {({ active }) => (
              <button
                className={`w-full flex items-center gap-3 px-4 py-2 text-sm ${active ? 'bg-white/10' : ''} text-gray-300`}
              >
                <DocumentDuplicateIcon className="h-4 w-4 text-gray-500" />
                Duplicate board
              </button>
            )}
          </Menu.Item>
          <Menu.Item>
            {({ active }) => (
              <button
                className={`w-full flex items-center gap-3 px-4 py-2 text-sm ${active ? 'bg-white/10' : ''} text-gray-300`}
              >
                <ArrowDownTrayIcon className="h-4 w-4 text-gray-500" />
                Export to Excel
              </button>
            )}
          </Menu.Item>
          <div className="my-1 border-t border-white/10" />
          <Menu.Item>
            {({ active }) => (
              <button
                className={`w-full flex items-center gap-3 px-4 py-2 text-sm ${active ? 'bg-red-500/10' : ''} text-red-400`}
              >
                <TrashIcon className="h-4 w-4" />
                Delete board
              </button>
            )}
          </Menu.Item>
        </Menu.Items>
      </Transition>
    </Menu>
  );
}
